import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  BarChart3, 
  BookOpen, 
  BookMarked, 
  Globe, 
  TrendingUp,
  X,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import Statistics from './Statistics';
import MangaStatistics from './MangaStatistics';
import GlobalStatistics from './GlobalStatistics';
import AdvancedStatistics from './AdvancedStatistics';
import StatisticsDashboard from './StatisticsDashboard';

interface StatisticsNavigatorProps {
  initialView?: 'dashboard' | 'main' | 'manga' | 'global' | 'advanced';
  hideHeader?: boolean;
}

type StatisticsView = 'dashboard' | 'main' | 'manga' | 'global' | 'advanced';

const StatisticsNavigator: React.FC<StatisticsNavigatorProps> = ({ initialView = 'dashboard', hideHeader = false }) => {
  const [currentView, setCurrentView] = useState<StatisticsView>(initialView);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const views = {
    dashboard: {
      title: 'Dashboard',
      icon: BarChart3,
      color: 'from-indigo-500 to-indigo-600',
      component: StatisticsDashboard
    },
    main: {
      title: 'Estadísticas Principales',
      icon: BarChart3,
      color: 'from-blue-500 to-blue-600',
      component: Statistics
    },
    manga: {
      title: 'Estadísticas de Manga',
      icon: BookMarked,
      color: 'from-purple-500 to-purple-600',
      component: MangaStatistics
    },
    global: {
      title: 'Estadísticas Globales',
      icon: Globe,
      color: 'from-green-500 to-green-600',
      component: GlobalStatistics
    },
    advanced: {
      title: 'Estadísticas Avanzadas',
      icon: TrendingUp,
      color: 'from-orange-500 to-orange-600',
      component: () => <AdvancedStatistics isOpen={true} onClose={() => {}} />
    }
  };

  const CurrentComponent = views[currentView].component;

  const navigateTo = (view: StatisticsView) => {
    setCurrentView(view);
    if (isFullscreen) {
      setIsFullscreen(false);
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  return (
    <div className={`${isFullscreen ? 'fixed inset-0 z-50 bg-white dark:bg-slate-900' : 'relative'}`}>
      {/* Header de navegación - solo se muestra si hideHeader es false */}
      {!hideHeader && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`bg-gradient-to-r ${views[currentView].color} text-white p-4 ${isFullscreen ? 'sticky top-0 z-10' : ''}`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigateTo('dashboard')}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors duration-200"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              
              <div className="flex items-center space-x-3">
                {React.createElement(views[currentView].icon, { className: "h-6 w-6" })}
                <h1 className="text-xl font-bold">{views[currentView].title}</h1>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={toggleFullscreen}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors duration-200"
                title={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
              >
                {isFullscreen ? (
                  <X className="h-5 w-5" />
                ) : (
                  <BookOpen className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>

          {/* Barra de navegación */}
          <div className="mt-4 flex items-center space-x-2 overflow-x-auto pb-2">
            {Object.entries(views).map(([key, view]) => (
              <motion.button
                key={key}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigateTo(key as StatisticsView)}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 whitespace-nowrap ${
                  currentView === key
                    ? 'bg-white text-slate-900 shadow-lg'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                <div className="flex items-center space-x-2">
                  {React.createElement(view.icon, { className: "h-4 w-4" })}
                  <span>{view.title}</span>
                </div>
              </motion.button>
            ))}
          </div>
        </motion.div>
      )}

      {/* Contenido principal */}
      <div className={`${isFullscreen ? 'h-full overflow-y-auto' : ''}`}>
        <motion.div
          key={currentView}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
          className="p-4"
        >
          <CurrentComponent />
        </motion.div>
      </div>

      {/* Indicador de progreso */}
      <div className="fixed bottom-4 right-4 z-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-3 py-2 rounded-full shadow-lg"
        >
          <div className="flex items-center space-x-2 text-sm">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span>Estadísticas activas</span>
          </div>
        </motion.div>
      </div>


    </div>
  );
};

export default StatisticsNavigator;